<template>
  <cf-validated-form
    name="giftCardMerge"
    :extra-validations="extraValidations"
    @valid-submit="confirmMerge"
  >
    <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-y-16">
      <Teleport
        defer
        to="#portal-modal-header-target"
      >
        Gift Card Merge
      </Teleport>

      <p class="cf-subtitle cf-subtitle--2">
        Merge the balance of two or more gift cards by making your selections below.
      </p>

      <div
        v-if="merchant.giftCardConfiguration.removeCardAfterMerge"
        class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-center cf-flex--gap-10 cf-alt-background"
      >
        <span class="cf-icon cf-icon--red far fa-exclamation-circle" />

        <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
          Any cards that you merge "from" will be unregistered and removed from your account!
        </p>
      </div>

      <div class="cf-flex cf-flex--column cf-flex--gap-8">
        <h4 class="cf-title cf-title--4 cf-text-align-left">
          Transfer From
        </h4>

        <p
          v-if="validTransferFromCards.length === 0"
          class="cf-subtitle cf-subtitle cf-subtitle--red"
        >
          You don't have any valid gift cards to merge from.
        </p>

        <div
          v-else
          class="cf-card-list"
        >
          <label
            v-for="card in validTransferFromCards"
            :key="card.cardId"
            class="cf-card cf-card--clickable cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between cf-padding-14"
            :for="`transferFromCard${card.cardId}`"
          >
            <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-12">
              <cf-card-type-icon
                payment-type="GiftCard"
                size="small"
                :card-design-url="card.design.url"
              />

              <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
                •••• {{ card.cardNumberLastFour }}
              </p>

              <!-- <span
                v-if="card.autoReload && card.autoReload.enabled"
                v-tippy
                class="cf-icon fa-repeat primary"
                content="Auto Reload Enabled"
              /> -->
            </div>

            <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-12">
              <p class="cf-subtitle cf-subtitle--2">
                {{ dollars(card.amount) }}
              </p>

              <div class="cf-checkbox-input cf-checkbox-input--default cf-checkbox-input--no-label-text">
                <div class="cf-checkbox-input__option">
                  <input
                    :id="`transferFromCard${card.cardId}`"
                    class="cf-checkbox-input__input"
                    type="checkbox"
                    name="transferFromCard"
                    :checked="transferFromCardIds.includes(card.cardId)"
                    @input="transferFromCardIdHandler(card.cardId)"
                  >

                  <div class="cf-checkbox-input__label" />
                </div>
              </div>
            </div>
          </label>
        </div>
      </div>

      <div class="cf-flex cf-flex--column cf-flex--gap-8">
        <h4 class="cf-title cf-title--4 cf-text-align-left">
          Transfer To
        </h4>

        <cf-select-input
          v-model="transferToCardId"
          label="Gift Card"
          name="transferToCard"
          :searchable="false"
          :values="transferToSelectOptions"
          :validations="{
            required: true
          }"
        >
          <template #placeholder>
            <p class="cf-subtitle cf-subtitle">
              Select a Gift Card...
            </p>
          </template>

          <template #selected="{ option }">
            <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between cf-full-width">
              <div class="cf-flex cf-flex--row cf-flex--gap-12">
                <cf-card-type-icon
                  payment-type="GiftCard"
                  size="small"
                  :card-design-url="option.data.card.design.url"
                />

                <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
                  {{ option.display }}
                </p>
              </div>

              <!-- <span
                v-if="option.data.card.autoReload && option.data.card.autoReload.enabled"
                class="cf-icon cf-icon--brand-primary fa-repeat"
              /> -->

              <p class="cf-subtitle cf-subtitle--2">
                {{ dollars(option.data.card.amount) }}
              </p>
            </div>
          </template>

          <template #option="{ option }">
            <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between cf-full-width">
              <div class="cf-flex cf-flex--row cf-flex--gap-12">
                <cf-card-type-icon
                  payment-type="GiftCard"
                  size="small"
                  :card-design-url="option.data.card.design.url"
                />

                <p class="cf-subtitle cf-subtitle--2">
                  {{ option.display }}
                </p>
              </div>

              <p class="cf-subtitle cf-subtitle--2">
                {{ dollars(option.data.card.amount) }}
              </p>
            </div>
          </template>
        </cf-select-input>
      </div>

      <transition name="fade-down-only-in">
        <div
          v-if="merchant.giftCardConfiguration.maximumCardAmount && mergedTotal > merchant.giftCardConfiguration.maximumCardAmount"
          class="cf-subtitle cf-subtitle--2 cf-subtitle--red"
        >
          The new balance must not exceed {{ dollars(merchant.giftCardConfiguration.maximumCardAmount, { trimZeros: true }) }}
        </div>
      </transition>

      <template v-if="merchant.giftCardConfiguration.requireCredentialsOnMergeGiftCards">
        <div
          class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-center cf-flex--gap-10 cf-alt-background"
        >
          <span class="cf-icon cf-icon--red far fa-exclamation-circle" />

          <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
            For added security, please verify your account password to merge your gift cards:
          </p>
        </div>

        <cf-text-input
          v-model="password"
          type="password"
          label="Verify Password"
          name="verifyPassword"
          :validations="{required: true }"
        />
      </template>

      <div class="cf-grid cf-grid--2-grid-column-equal">
        <div class="cf-flex cf-flex--column cf-flex--align-start">
          <p class="cf-subtitle cf-subtitle--2">
            New Balance
          </p>

          <h2 :class="['cf-title cf-title--2', { 'cf-title--brand-primary' : mergedTotal > merchant.giftCardConfiguration.maximumCardAmount }]">
            {{ dollars(mergedTotal) }}
          </h2>
        </div>

        <button
          class="cf-button cf-button--primary cf-button--lg"
          :class="{ 'cf-button--loading': isMergingCards }"
          :disabled="!transferFromCardIdsSanitized.length || exceedsMaximumCardAmount"
          type="submit"
        >
          Merge Cards
        </button>
      </div>
    </div>
  </cf-validated-form>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';

  import dollars from '@/filters/dollars';

  import CfCardTypeIcon from '@/view/components/shared/cf-card-type-icon/index.vue';
  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfSelectInput from '@/view/components/shared/cf-select-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';


  export default {
    name: 'GiftCardMerge',

    components: {
      CfCardTypeIcon,
      CfValidatedForm,
      CfSelectInput,
      CfTextInput
    },

    props: {
      cardId: {
        type: String,
        default: null
      }
    },

    data() {
      return {
        isMergingCards: false,
        password: null,
        transferToCardId: null,
        transferFromCardIds: []
      };
    },

    computed: {
      ...mapGetters('giftCards', ['giftCards']),
      ...mapGetters('merchant', ['merchant']),

      exceedsMaximumCardAmount() {
        return this.merchant.giftCardConfiguration.maximumCardAmount && this.mergedTotal > this.merchant.giftCardConfiguration.maximumCardAmount;
      },

      mergedTotal() {
        let total = 0;

        [this.transferToCardId, ...this.transferFromCardIdsSanitized].forEach((cardId) => {
          if (!cardId) {
            return;
          }

          const card = this.giftCards.savedCards.find(c => c.cardId === cardId);

          if (!card) {
            return;
          }

          total += card.amount;
        });

        return total;
      },

      transferToSelectOptions() {
        return this.giftCards.savedCards.map(card => ({
          data: { card },
          display: `•••• ${card.cardNumberLastFour}`,
          value: card.cardId
        }));
      },

      transferFromCardIdsSanitized() {
        return this.transferFromCardIds.filter(cardId => cardId !== this.transferToCardId);
      },

      validTransferFromCards() {
        return this.giftCards.savedCards.filter(card => card.cardId !== this.transferToCardId && card.amount > 0);
      },

      autoReloadTransferFromCards() {
        return this.validTransferFromCards.filter(card => this.transferFromCardIds.includes(card.cardId) && card.autoReload?.enabled);
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      dollars,

      createdHandler() {
        this.transferToCardId = this.cardId;
      },

      extraValidations() {
        let isValid = true;

        if (this.transferFromCardIdsSanitized.length === 0) {
          isValid = false;

          events.emit('notification', {
            type: 'error',
            message: 'Select at least one card to transfer from.'
          });
        }

        return isValid;
      },

      confirmMerge() {
        if (this.autoReloadTransferFromCards.length) {
          const isMultiple = this.autoReloadTransferFromCards.length > 1;
          const cardNumbers = this.autoReloadTransferFromCards
            .map(c => `<span class="primary bold">${c.cardNumberLastFour}</span>`)
            .join(', ')
            .replace(/,(?=[^,]+$)/, ' and'); /* replaces last comma with "and" */

          this.$openModal('confirmation', {
            title: `Active Auto Reload${isMultiple ? 's' : ''}`,
            primaryText: `
              The gift card${isMultiple ? 's' : ''} ending in ${cardNumbers} ${isMultiple ? 'have' : 'has'}
              auto reload enabled. Merging ${isMultiple ? 'these cards' : 'this card'} will remove ${isMultiple ? 'their' : 'its'} auto reload settings.
            `.replace(/\s+/g, ' ').trim(),
            secondaryText: `Are you sure you'd like to merge ${isMultiple ? 'cards' : 'this card'}?`,
            ctaConfirmText: 'Yes, Merge',
            onConfirm: this.mergeCards
          });
        }
        else {
          this.mergeCards();
        }
      },

      async mergeCards() {
        try {
          this.isMergingCards = true;

          await this.$store.dispatch('giftCards/mergeCards', {
            destinationCardId: this.transferToCardId,
            password: this.password,
            sourceCardIds: this.transferFromCardIdsSanitized
          });

          this.$closeModal('giftCardDetails');
          this.$closeModal('giftCardMerge');
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isMergingCards = false;
        }
      },

      transferFromCardIdHandler(cardId) {
        const isSelected = this.transferFromCardIds.includes(cardId);

        if (isSelected) {
          this.transferFromCardIds = this.transferFromCardIds.filter(id => id !== cardId);
        }

        else {
          this.transferFromCardIds.push(cardId);
        }
      }
    }
  };
</script>
